<template>
  <div class="maint-form">
    <div class="maint-form__header">
      <div class="maint-form__title">{{ $t('maintenance.reportTitle') }}</div>
      <div class="maint-form__item">{{ item?.item?.name ?? `#${item?.id}` }}</div>
    </div>

    <form @submit.prevent="onSubmit" class="maint-form__body">

      <!-- Photo capture (optional) -->
      <BaseFormField
        :label="$t('maintenance.photoLabel')"
        :hint="$t('maintenance.photoOptional')"
        :error="fieldErrors.photo || ''"
      >
        <div v-if="photoPreview" class="maint-photo-preview">
          <img :src="photoPreview" :alt="$t('ui.damagePhoto')" />
          <button type="button" class="maint-photo-clear" @click="clearPhoto">{{ $t('maintenance.removePhoto') }}</button>
        </div>

        <div v-else class="maint-photo-trigger" @click="triggerFileInput">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="2" y="7" width="20" height="15" rx="2"/>
            <path d="M16 3l-4 4-4-4"/>
            <circle cx="12" cy="14" r="3"/>
          </svg>
          <span>{{ $t('maintenance.photoTap') }}</span>
          <BaseInput
            ref="fileInput"
            type="file"
            accept="image/*"
            capture="environment"
            class="hidden"
            :aria-label="$t('maintenance.photoLabel')"
            @change="onFileChange"
          />
        </div>
      </BaseFormField>

      <!-- Short note (required) -->
      <BaseFormField
        :label="$t('maintenance.damageNoteLabel')"
        required
        :error="fieldErrors.note || ''"
      >
        <BaseTextarea
          v-model="note"
          :rows="3"
          :placeholder="$t('maintenance.damageNotePlaceholder')"
          maxlength="500"
        />
      </BaseFormField>
      <div class="text-xs text-[var(--color-muted)] text-right -mt-2">{{ note.length }}/500</div>

      <!-- API error -->
      <div v-if="submitError" class="maint-error">{{ submitError }}</div>

      <!-- Actions (full-width touch targets; keep feature layout) -->
      <div class="maint-form__actions">
        <button type="button" class="maint-btn maint-btn--cancel" @click="$emit('cancel')" :disabled="submitting">
          {{ $t('common.cancel') }}
        </button>
        <button type="submit" class="maint-btn maint-btn--submit" :disabled="submitting || !isValid">
          <svg v-if="submitting" class="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-opacity=".3"/>
            <path d="M12 2a10 10 0 0 1 10 10"/>
          </svg>
          {{ submitting ? $t('maintenance.submitting') : $t('maintenance.submitAndMark') }}
        </button>
      </div>

    </form>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { postReturn, postMaintenanceReport } from '@/api/api.js'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'

const props = defineProps({
  item: { type: Object, required: true },
})

const emit = defineEmits(['submitted', 'cancel'])
const { t } = useI18n()

const note         = ref('')
const photoBase64  = ref(null)
const photoPreview = ref(null)
const submitting   = ref(false)
const submitError  = ref(null)
const fieldErrors  = ref({})
const fileInput    = ref(null)

const isValid = computed(() => note.value.trim().length >= 3)

function triggerFileInput() {
  fileInput.value?.click()
}

function clearPhoto() {
  photoBase64.value  = null
  photoPreview.value = null
  const el = fileInput.value?.el
  if (el) el.value = ''
}

function onFileChange(e) {
  const file = e.target.files?.[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = (ev) => {
    photoPreview.value = ev.target.result         // data URL for preview
    photoBase64.value  = ev.target.result         // send base64 to API
  }
  reader.readAsDataURL(file)
}

async function onSubmit() {
  fieldErrors.value = {}
  if (!note.value.trim())  { fieldErrors.value.note  = t('maintenance.noteRequired'); return }

  submitting.value = true
  submitError.value = null

  try {
    // 1. Post return with 'damaged' condition
    await postReturn({
      item_metadata_id: props.item.id,
      _checkout_id:     props.item._checkout_id ?? null,
      condition:        'damaged',
    })

    // 2. Create maintenance ticket
    await postMaintenanceReport({
      item_metadata_id: props.item.id,
      note: note.value.trim(),
      photo_base64: photoBase64.value,
    })

    emit('submitted')
  } catch (e) {
    submitError.value = e?.data?.error ?? t('maintenance.submitFailed')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.maint-form {
  background: #111827;
  border: 1px solid #374151;
  border-radius: 1rem;
  overflow: hidden;
  margin: .75rem 1rem;
}

.maint-form__header {
  background: #7f1d1d;
  padding: 1rem 1.25rem;
}

.maint-form__title { font-size: .8rem; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #fca5a5; }
.maint-form__item  { font-size: 1.1rem; font-weight: 700; color: #fff; margin-top: .2rem; }

.maint-form__body { padding: 1.25rem; display: flex; flex-direction: column; gap: 1.125rem; }

/* Photo */
.maint-photo-trigger {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  border: 2px dashed #374151;
  border-radius: .75rem;
  padding: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  font-size: .9rem;
  transition: border-color .15s, color .15s;
}
.maint-photo-trigger:hover { border-color: #4b5563; color: #9ca3af; }
.maint-photo-trigger svg { width: 36px; height: 36px; }

.maint-photo-preview { position: relative; }
.maint-photo-preview img {
  width: 100%;
  border-radius: .625rem;
  max-height: 220px;
  object-fit: cover;
  display: block;
}
.maint-photo-clear {
  position: absolute;
  top: .5rem; right: .5rem;
  background: rgba(0,0,0,.65);
  border: none;
  color: #f9fafb;
  border-radius: .375rem;
  padding: .3rem .65rem;
  font-size: .8rem;
  cursor: pointer;
}

/* Errors */
.maint-error { font-size: .875rem; color: #f87171; background: #1c0a0a; border: 1px solid #7f1d1d; border-radius: .5rem; padding: .625rem .875rem; }

/* Actions */
.maint-form__actions { display: flex; gap: .75rem; padding-top: .25rem; }

.maint-btn {
  flex: 1;
  padding: .875rem;
  border: none;
  border-radius: .625rem;
  font-size: .95rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  transition: filter .1s;
}
.maint-btn:disabled { opacity: .5; cursor: not-allowed; }

.maint-btn--cancel { background: #1f2937; color: #d1d5db; }
.maint-btn--cancel:hover:not(:disabled) { background: #374151; }

.maint-btn--submit { background: #7f1d1d; color: #fca5a5; }
.maint-btn--submit:hover:not(:disabled) { filter: brightness(1.2); }

.spinner { width: 18px; height: 18px; animation: spin .75s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
